
import { useTranslation } from 'react-i18next';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useStaff } from '@/api/staff';
import { Skeleton } from '@/components/ui/skeleton';

interface StaffSelectorProps {
  value: string;
  onChange: (value: string) => void;
  allowClear?: boolean;
  placeholder?: string;
  disabled?: boolean;
}

export function StaffSelector({
  value,
  onChange,
  allowClear = true,
  placeholder,
  disabled,
}: StaffSelectorProps) {
  const { t } = useTranslation();
  const { data: staffMembers, isLoading, error } = useStaff();

  // Filter staff to only show relevant user types
  const eligibleStaff = staffMembers?.filter((staff) =>
    ['trainer', 'admin', 'reception', 'owner'].includes(staff.userType.toLowerCase())
  );

  // Handle value change - convert special "none" value to empty string
  const handleValueChange = (newValue: string) => {
    if (newValue === '__none__') {
      onChange('');
    } else {
      onChange(newValue);
    }
  };

  // Display value - convert empty string to "none" for the select
  const displayValue = value === '' ? '__none__' : value;

  if (isLoading) {
    return <Skeleton className="h-10 w-full" />;
  }

  if (error) {
    return (
      <div className="text-sm text-destructive">
        {t('common.errorLoadingStaff', 'Error loading staff members')}
      </div>
    );
  }

  return (
    <Select value={displayValue} onValueChange={handleValueChange} disabled={disabled}>
      <SelectTrigger>
        <SelectValue
          placeholder={placeholder || t('classes.selectInstructor', 'Select instructor...')}
        />
      </SelectTrigger>
      <SelectContent>
        {allowClear && (
          <SelectItem value="__none__">
            {t('classes.noInstructor', 'No instructor')}
          </SelectItem>
        )}
        {eligibleStaff?.map((staff) => (
          <SelectItem key={staff._id || staff.id} value={staff._id || staff.id || '__none__'}>
            {staff.name} {staff.surname} - {staff.userType}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
